import React, { useEffect, useRef, useState } from 'react'
import { Button, Card, Collapse, Input, Typography } from 'antd'
import { SafetyCertificateOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '~/Store'
import { ApiKeyActions, getIsLoadingProducts, getProducts } from '~/Store/ApiKey'
import { RolesActions } from '~/Store/Role'
import {
  getIsLoadingRoleClaims,
  getIsSavingRole,
  getRoleClaims,
  getRoleProductClaims,
  getRoleSaved,
} from '~/Store/Role/selectors'
import { RolesResponse } from '~/Services/AdminRolesService'
import { RoleProductHeader } from './RoleProductHeader'
import { RoleProductPermissions } from './RoleProductPermissions'

const { Panel } = Collapse

export const EditServicePrincipalRole = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation<{ role?: RolesResponse } | undefined>()
  const role = location.state?.role
  const dispatch = useAppDispatch()

  const products = useAppSelector(getProducts)
  const isLoadingProducts = useAppSelector(getIsLoadingProducts)
  const roleClaims = useAppSelector(getRoleClaims)
  const isLoadingRoleClaims = useAppSelector(getIsLoadingRoleClaims)
  const isSaving = useAppSelector(getIsSavingRole)
  const roleSaved = useAppSelector(getRoleSaved)
  const productClaimsByProductId = useAppSelector(state => state.roles.productClaims)

  const [roleName, setRoleName] = useState(role?.name ?? '')
  const [expandedProductIds, setExpandedProductIds] = useState<string[]>([])
  const [selectedClaimIds, setSelectedClaimIds] = useState<
    Record<string, string[]>
  >({})
  const claimsInitialized = useRef(false)

  useEffect(() => {
    if (!role) {
      history.replace('/Organizations/ServicePrincipalRoles')
      return
    }
    dispatch(ApiKeyActions.getProducts())
    dispatch(RolesActions.getRoleClaims(role.id))
    return () => {
      dispatch(RolesActions.resetRoleClaims())
    }
  }, [])

  useEffect(() => {
    if (roleSaved) {
      dispatch(RolesActions.resetRoleSaved())
      history.goBack()
    }
  }, [roleSaved])

  useEffect(() => {
    if (!role) return
    products.forEach(product => {
      dispatch(RolesActions.getProductClaims(product.guid))
    })
  }, [role, products])

  // The role's current claims (GET .../roles/{id}/claims) come back as a flat
  // list keyed by ProductClaimId with no ProductId — cross-reference each id
  // against each product's own full claim catalog to know which panel it
  // belongs to, then seed the selection once (ref guards against clobbering
  // the user's own edits on a later re-render).
  useEffect(() => {
    if (claimsInitialized.current || !role || !roleClaims) return
    if (products.length === 0) return
    const allLoaded = products.every(
      p => productClaimsByProductId[p.guid] !== undefined,
    )
    if (!allLoaded) return

    const assignedIds = new Set(roleClaims.claims.map(c => c.productClaimId))
    const grouped: Record<string, string[]> = {}
    products.forEach(product => {
      const claims = productClaimsByProductId[product.guid] || []
      const matched = claims
        .filter(c => assignedIds.has(c.guid))
        .map(c => c.guid)
      if (matched.length) grouped[product.guid] = matched
    })
    setSelectedClaimIds(grouped)
    setExpandedProductIds(Object.keys(grouped))
    claimsInitialized.current = true
  }, [role, roleClaims, products, productClaimsByProductId])

  if (!role) return null

  const totalServicesSelected = Object.values(selectedClaimIds).filter(
    ids => ids.length > 0,
  ).length

  const toggleProduct = (
    productId: string,
    checked: boolean,
    allClaimIds: string[],
  ) => {
    setSelectedClaimIds(prev => ({
      ...prev,
      [productId]: checked ? allClaimIds : [],
    }))
    if (checked && !expandedProductIds.includes(productId)) {
      setExpandedProductIds(prev => [...prev, productId])
    }
  }

  const toggleClaim = (productId: string, claimId: string, checked: boolean) => {
    setSelectedClaimIds(prev => {
      const current = prev[productId] || []
      return {
        ...prev,
        [productId]: checked
          ? [...current, claimId]
          : current.filter(id => id !== claimId),
      }
    })
  }

  const deselectAll = (productId: string) => {
    setSelectedClaimIds(prev => ({ ...prev, [productId]: [] }))
  }

  const cancelClick = () => {
    history.goBack()
  }

  const saveClick = () => {
    if (!roleName.trim()) return
    const productClaimIds = Object.values(selectedClaimIds).flat()
    dispatch(
      RolesActions.saveRole({
        roleId: role.id,
        roleName: role.name,
        newRolename: roleName.trim(),
        type: 'ServicePrincipal',
        productClaimIds,
      }),
    )
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '20px',
        }}
      >
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            <SafetyCertificateOutlined style={{ marginRight: '8px' }} />
            {t('admin.roleEditTitle')}
          </Typography.Title>
        </div>
        <div>
          <Button onClick={cancelClick} style={{ marginRight: '10px' }}>
            {t('cancel')}
          </Button>
          <Button
            type="primary"
            loading={isSaving}
            disabled={!roleName.trim()}
            onClick={saveClick}
          >
            {t('save')}
          </Button>
        </div>
      </div>

      <Card style={{ marginBottom: '20px' }}>
        <div style={{ marginBottom: '6px' }}>
          {t('admin.roleName')} <span style={{ color: 'red' }}>*</span>
        </div>
        <Input value={roleName} onChange={evt => setRoleName(evt.target.value)} />
      </Card>

      <Card
        title={t('admin.akServiceProviders')}
        extra={
          <span>
            {t('admin.akSelectedCount', { count: totalServicesSelected })}
          </span>
        }
        loading={isLoadingProducts || isLoadingRoleClaims}
      >
        <Collapse
          activeKey={expandedProductIds}
          onChange={keys => setExpandedProductIds(keys as string[])}
        >
          {products.map(product => (
            <Panel
              key={product.guid}
              header={
                <RoleProductHeader
                  product={product}
                  selectedClaimIds={selectedClaimIds[product.guid] || []}
                  onToggleProduct={(checked, allClaimIds) =>
                    toggleProduct(product.guid, checked, allClaimIds)
                  }
                />
              }
            >
              <RoleProductPermissions
                productId={product.guid}
                selectedClaimIds={selectedClaimIds[product.guid] || []}
                onToggleClaim={(claimId, checked) =>
                  toggleClaim(product.guid, claimId, checked)
                }
                onDeselectAll={() => deselectAll(product.guid)}
              />
            </Panel>
          ))}
        </Collapse>
      </Card>
    </div>
  )
}
